import {
    RiAlertLine,
    RiCheckboxCircleLine, RiCloseCircleLine, RiSparkling2Line,
} from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement, useEffect,
    useState,
} from "react";
import {
    GetBillingDocument,
    GetBillingPaymentsDocument,
    GetUserAgentsDocument,
    PortalBillingSubscription,
    PortalBillingTierDuration, PortalBillingTierPrice, useBillingSubscriptionChangeMutation, useCreateBillingOrderMutation, useGetBillingQuery,
} from "../../../../__generated__/api";
import { ReactComponent as PatternCircles } from "../../../../assets/patterns/circles.svg";
import { ReactComponent as PatternFractal } from "../../../../assets/patterns/fractal.svg";
import { ReactComponent as PatternSpiral } from "../../../../assets/patterns/spiral.svg";
import Badge from "../../../../components/theme/badge/Badge";
import Button, { VariantTypes } from "../../../../components/theme/button/Button";
import ButtonTabs from "../../../../components/theme/button/ButtonTabs";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Icon from "../../../../components/theme/icon/Icon";
import SettingsContainer from "../../../../components/theme/settings";
import Text from "../../../../components/theme/text/Text";
import useArticleContext from "../../../../contexts/HelpContext";
import Styles from "./Billing.module.scss";

import { ReactComponent as IconCheck } from "../../../../assets/icons/billing-tier-checkmark.svg";
import { ReactComponent as IconCrown } from "../../../../assets/icons/billing-tier-crown.svg";
import { ReactComponent as IconEnterprise } from "../../../../assets/icons/billing-tier-enterprise.svg";
import { useTimedMessage } from "../../../../components/timed-message/TimedMessage";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import useRouting, { Paths } from "../../../../contexts/Routing";

export const IMAGES = [
    PatternCircles,
    PatternFractal,
    PatternSpiral,
];

export const ICONS = [
    IconCrown,
    IconCheck,
    IconEnterprise,
];

const BUTTONS = [
    {
        key: PortalBillingTierDuration.Month,
        text: "Monthly",
    },
    {
        key: PortalBillingTierDuration.Year,
        text: "Yearly",
    },
];

export const DURATION_TO_NAME = {
    [PortalBillingTierDuration.Month]: {
        text: "Monthly",
        priceText: "per month",
    },
    [PortalBillingTierDuration.Year]: {
        text: "Annually",
        priceText: "per year",
    },
};

export default function BillingCheckout({
    subscription,
}: {
    subscription?: PortalBillingSubscription
}): ReactElement {
    useArticleContext("urn:fq:article:67d9c5ea6d6c4cbd8ec38dcd75459521");
    const [duration, setDuration] = useState<PortalBillingTierDuration>(PortalBillingTierDuration.Month);
    const [seats, setSeats] = useState<number>(0);

    const { data, loading: isLoading } = useGetBillingQuery();

    const [createOrder] = useCreateBillingOrderMutation();
    const [updateSubscription] = useBillingSubscriptionChangeMutation();
    const { historyPush } = useRouting();

    useEffect(() => {
        if (data?.billing?.defaultQuantity) {
            setSeats(subscription?.seatCount ?? Math.round(data.billing.defaultQuantity / 10) * 10);
        }
    }, [data?.billing, subscription]);

    const { element: errorElement, show: showError } = useTimedMessage({
        variant: "danger",
        prefixIcon: <RiAlertLine />,
    });

    const subscribe = async (encryptedPriceId: string) => {
        try {
            const order = await createOrder({
                variables: {
                    order: {
                        lines: [
                            {
                                encryptedPriceId,
                                quantity: seats,
                            },
                        ],
                    },
                },
            });
            if (order.data?.billingOrder?.redirectUrl) {
                window.location.href = order.data.billingOrder.redirectUrl;
            }
        } catch (err) {
            showError("An error occured while checking out. Please try again later.");
            LoggingHandler.instance.error({ err }, "Error while checking out");
        }
    };

    const changeSubscription = async (encryptedPriceId: string) => {
        try {
            await updateSubscription({
                refetchQueries: [
                    { query: GetBillingDocument },
                    { query: GetBillingPaymentsDocument },
                    { query: GetUserAgentsDocument },
                ],
                variables: {
                    change: {
                        encryptedSubscriptionId: subscription?.encryptedSubscriptionId as string,
                        encryptedPriceId,
                        quantity: seats,
                    },
                },
            });
            historyPush(Paths.ADMINISTRATOR_SETTING, { setting: "billing" });
        } catch (err) {
            showError(`An error occured while updating your subscription. Please try again later.
                     If you still need help, please contact support.`);
            LoggingHandler.instance.error({ err }, "Error while checking out");
        }
    };

    const currentTierIndex = subscription
        ? data?.billing?.tiers?.findIndex(tier => tier.encryptedTierId === subscription?.tier.encryptedTierId)
        : undefined;

    const currentTier = subscription?.tier.encryptedTierId
        ? data?.billing?.tiers?.find(tier => tier.encryptedTierId === subscription?.tier.encryptedTierId)
        : undefined;

    const currentPrice = subscription?.price.encryptedPriceId
        ? currentTier?.prices.find(current => current.encryptedPriceId === subscription?.price.encryptedPriceId)
        : undefined;

    const currentDuration = currentPrice?.duration ?? duration;
    return (
        <SettingsContainer navigation="none">

            <div className={Styles["top-section"]}>
                <SettingsContainer.Section title="Number of Agents" shouldDisplayTitle={false}>
                    <SettingsContainer.Setting
                        isLoading={isLoading}
                        title="Number of Agents"
                        description="How many agents you need to start with?"
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isHideTitle
                    >

                        <div className={Styles.header}>
                            <Badge variant="primary" size="large">1</Badge>
                            <div>
                                <Text as="h2" textVariant="subtitle-2">
                                    Number of Agents
                                </Text>
                                <Text as="h2" textVariant="body-1-regular" colorVariant="body-secondary">
                                    How many agents you need to start with?
                                </Text>
                            </div>
                        </div>
                        <div className={Styles.incrementer}>
                            <Button.Subtract
                                size="small"
                                variant="outline-gray"
                                onClick={() => setSeats(seat => Math.max(seat - 1, 1))}
                            />
                            <FormGroup
                                type="number"
                                value={seats}
                                size="small"
                                onChange={event => setSeats(Math.min(Math.max(parseInt(event.target.value, 10), 1), 1000))}
                            />
                            <Button.Add
                                size="small"
                                variant="outline-gray"
                                onClick={() => setSeats(seat => seat + 1)}
                            />
                        </div>

                    </SettingsContainer.Setting>

                </SettingsContainer.Section>
                <SettingsContainer.Section title="Choose your Contract Period" shouldDisplayTitle={false}>
                    <SettingsContainer.Setting
                        isLoading={isLoading}
                        title="Number of Agents"
                        description="How many agents you need to start with?"
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isHideTitle
                    >

                        <div className={Styles.header}>
                            <Badge variant="primary" size="large">2</Badge>
                            <div>
                                <Text as="h2" textVariant="subtitle-2">
                                    Choose your Contract Period
                                </Text>
                                <Text as="h2" textVariant="body-1-regular" colorVariant="body-secondary">
                                    Choose your Contract Period
                                </Text>
                            </div>
                        </div>

                        <ButtonTabs
                            values={BUTTONS}
                            value={currentDuration}
                            isDisabled={currentPrice != null}
                            onChange={value => setDuration(value.key as PortalBillingTierDuration)}
                        />

                    </SettingsContainer.Setting>

                </SettingsContainer.Section>

            </div>

            <SettingsContainer.Section title="Choose your Plan" shouldDisplayTitle={false}>
                <SettingsContainer.Setting
                    isLoading={isLoading}
                    title="Choose your Plan"
                    description="How many agents you need to start with?"
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isHideTitle
                >

                    <div className={Styles.header}>
                        <Badge variant="primary" size="large">3</Badge>
                        <div>
                            <Text as="h2" textVariant="subtitle-2">
                                Choose your Plan
                            </Text>
                            <Text as="h2" textVariant="body-1-regular" colorVariant="body-secondary">
                                Select the subscription that fits your needs and unlock powerful features
                            </Text>
                        </div>
                    </div>
                    <div className={Styles.tiers}>
                        {data?.billing?.tiers?.map((tier, index) => {
                            const price = tier.prices
                                .find(current => current.duration === currentDuration) as PortalBillingTierPrice;
                            if (!price) {
                                return null;
                            }
                            const durationName = DURATION_TO_NAME[price.duration];
                            const formatter = new Intl.NumberFormat(undefined, {
                                style: "currency",
                                currency: price.currency,
                                minimumFractionDigits: 0,
                            });

                            const isCurrent = currentTier === tier;
                            let canUpgrade = !isCurrent;
                            const isEnabled = price.minimumNumberOfUsers <= seats;

                            let text;
                            let buttonVariant: VariantTypes = "outline-blue";
                            if (!isEnabled) {
                                text = `Minimum ${price.minimumNumberOfUsers} agents`;
                            } else if (isCurrent && subscription) {
                                text = "Current Subscription";
                                const diffSeats = seats - (subscription.seatCount ?? 0);
                                if (diffSeats > 0) {
                                    text = `Add ${diffSeats} agents`;
                                    canUpgrade = true;
                                } else if (diffSeats < 0) {
                                    text = `Remove ${Math.abs(diffSeats)} agents`;
                                    canUpgrade = true;
                                }
                            } else if (currentTierIndex != null && currentTierIndex < index) {
                                text = "Upgrade";
                                buttonVariant = "primary";
                            } else if (currentTierIndex != null && currentTierIndex > index) {
                                text = "Downgrade";
                                buttonVariant = "outline-gray";
                            } else {
                                text = `Get ${tier.name}`;
                                buttonVariant = "primary";
                            }
                            const patternIndex = index % IMAGES.length;
                            const Pattern = IMAGES[patternIndex];

                            const iconIndex = index % ICONS.length;
                            const IconImage = ICONS[iconIndex];

                            return (
                                <div
                                    className={classNames(Styles.tier, Styles[`tier-${index + 1}`])}
                                    key={tier.name}
                                >
                                    <Pattern className={classNames(Styles.pattern, Styles[`pattern-${patternIndex + 1}`])} />
                                    <div className={Styles["tier-header"]}>
                                        <Icon
                                            icon={<IconImage />}
                                            background="primary"
                                            className={classNames(Styles.icon, Styles[`icon-${iconIndex + 1}`])}
                                            isRounded
                                            size="auto"
                                        />

                                        {price.isRecommended && (
                                            <Text as="small" textVariant="body-2-medium" className={Styles.recommended}>
                                                <Icon icon={<RiSparkling2Line />} size="small" />
                                                {" "}
                                                Most Popular
                                            </Text>
                                        )}
                                    </div>

                                    <div className={Styles["tier-line"]}>
                                        <Text as="h2" textVariant="subtitle-2">
                                            {tier.name}
                                        </Text>
                                        {" "}
                                        <Badge variant="primary">
                                            Billed
                                            {" "}
                                            {durationName.text}
                                        </Badge>
                                    </div>
                                    <Text
                                        as="p"
                                        textVariant="body-2-regular"
                                        colorVariant="body-secondary"
                                        className={Styles["tier-description"]}
                                    >
                                        {tier.description}
                                    </Text>
                                    <div className={Styles.price}>
                                        <h1>
                                            {formatter.format(price.price / 100)}
                                        </h1>
                                        <Text as="span" textVariant="body-2-regular" colorVariant="body-secondary">
                                            {" "}
                                            / per agent,
                                            {" "}
                                            {durationName.priceText}
                                        </Text>
                                    </div>
                                    <span>
                                        <Text
                                            as="span"
                                            textVariant="body-2-medium"
                                            colorVariant="body-secondary"
                                        >
                                            {formatter.format((price.price * (seats ?? 1)) / 100)}
                                        </Text>
                                        <Text
                                            as="span"
                                            textVariant="body-2-regular"
                                            colorVariant="body-secondary"
                                        >
                                            {" "}
                                            {durationName.priceText}
                                            {" "}
                                            for
                                            {" "}
                                            {seats ?? 1}
                                            {" "}
                                            agents
                                        </Text>
                                    </span>

                                    <Button
                                        variant={buttonVariant}
                                        onClick={() => (subscription
                                            ? changeSubscription(price.encryptedPriceId)
                                            : subscribe(price.encryptedPriceId))}
                                        disabled={!canUpgrade || !isEnabled}
                                    >
                                        {text}
                                    </Button>

                                    <ul className={Styles.benefits}>
                                        {tier.benefits.map(benefit => (
                                            <li>
                                                <RiCheckboxCircleLine />
                                                {" "}
                                                {benefit}
                                            </li>
                                        ))}
                                        {tier.excludedBenefits?.map(benefit => (
                                            <li className={Styles["benefit-exclude"]}>
                                                <RiCloseCircleLine />
                                                {" "}
                                                {benefit}
                                            </li>
                                        ))}
                                    </ul>

                                </div>
                            );
                        })}
                    </div>
                </SettingsContainer.Setting>

            </SettingsContainer.Section>
            {errorElement}
        </SettingsContainer>

    );
}

BillingCheckout.defaultProps = {
    subscription: undefined,
};
